import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import { formatNumberWithLocale } from "lib/helpers";
import type { Variant } from "@mui/material/styles/createTypography";

type IProps = {
  profit: number;
  risk: number;
  /** Permite cambiar “Annual Profit” por “Profit” sin tocar estilos */
  profitLabel?: string;
  /** Por si quieres renombrar “Risk” (opcional) */
  riskLabel?: string;
  profitLabelColor?: string;
  riskLabelColor?: string;
  profitColor?: string;
  riskColor?: string;
  profitBgcolor?: string;
  riskBgcolor?: string;
  profitBorderRadius?: string;
  riskBorderRadius?: string;
  variantTitle?: Variant;
  variant?: Variant;
  px?: number;
  py?: number;

  /** NUEVO: controla si añadir % y cuántos decimales */
  showProfitPercent?: boolean; // default = true (como antes)
  showRiskPercent?: boolean; // default = true (como antes)
  profitDecimals?: number; // default = 1 (como antes)
  riskDecimals?: number; // default = 0 (como antes)
};

/**
 * Box to display profit & risk
 * @component
 * @example
 * <ProfitRiskBox profit={50.54} risk={10} />
 */
const ProfitRiskBox = function ({
  profit,
  risk,
  profitLabel = "Annual Profit",
  riskLabel = "Risk",
  profitLabelColor = "secondary",
  riskLabelColor = "secondary",
  profitColor = "#11CC9A",
  riskColor = "#253E66",
  profitBgcolor = "#253E66",
  riskBgcolor = "#E5AA17",
  profitBorderRadius = "10px 0 0 10px",
  riskBorderRadius = "0 10px 0 0",
  variantTitle = "caption",
  variant = "body1",
  px = 1,
  py = 0.3,

  // NUEVO: defaults que mantienen el comportamiento original
  showProfitPercent = true,
  showRiskPercent = true,
  profitDecimals = 1,
  riskDecimals = 0,
}: IProps) {
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith("es") ? "es-ES" : "en-US";

  const fmt = (value: number, decimals: number) =>
    value.toLocaleString(lang, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });

  return (
    <Box display="flex">
      <Box display="flex" alignItems="center">
        <Box
          bgcolor={profitBgcolor}
          color={profitColor}
          display="flex"
          flexDirection="column"
          borderRadius={profitBorderRadius}
          px={px}
          mr="1px"
          py={py}
        >
          <Typography
            color={profitLabelColor}
            variant={variantTitle}
            fontWeight={700}
          >
            {profitLabel}
          </Typography>
          <Typography fontWeight={500} fontFamily="Poppins" variant={variant}>
            {profit > 0 && "+"}
            {fmt(profit, profitDecimals)}
            {showProfitPercent ? "%" : ""}
          </Typography>
        </Box>

        <Box
          bgcolor={riskBgcolor}
          color={riskColor}
          display="flex"
          flexDirection="column"
          borderRadius={riskBorderRadius}
          px={px}
          py={py}
        >
          <Typography color={riskLabelColor} variant={variantTitle}>
            {riskLabel}
          </Typography>
          <Typography fontWeight={500} fontFamily="Poppins" variant={variant}>
            {fmt(risk, riskDecimals)}
            {showRiskPercent ? "%" : ""}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default ProfitRiskBox;
